import {
  createRouter,
  createWebHistory,
  type RouteRecordRaw,
} from "vue-router";
import MainLayout from "@/components/layouts/MainLayout.vue";
import { useAuthStore } from "@/stores/auth";
import { useStudyContextStore } from "@/stores/studyContext";
import {
  CRF_VERSION_DETAIL_ROUTE_NAME,
  CRF_VERSION_DETAIL_ROUTE_PATH,
  CRF_VERSION_MANAGEMENT_ROUTE_NAME,
  CRF_VERSION_MANAGEMENT_ROUTE_PATH,
} from "./crfVersionRoutes";

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    redirect: "/dashboard",
  },
  {
    path: "/login",
    name: "Login",
    component: () => import("@/views/LoginView.vue"),
    meta: {
      title: "登录",
      requiresAuth: false,
    },
  },
  {
    path: "/forgot-password",
    name: "ForgotPassword",
    component: () => import("@/views/ForgotPasswordView.vue"),
    meta: {
      title: "忘记密码",
      requiresAuth: false,
    },
  },
  {
    path: "/reset-password",
    name: "ResetPassword",
    component: () => import("@/views/ResetPasswordView.vue"),
    meta: {
      title: "重置密码",
      requiresAuth: false,
    },
  },
  {
    path: "/",
    component: MainLayout,
    meta: {
      requiresAuth: true,
    },
    children: [
      {
        path: "dashboard",
        name: "Dashboard",
        component: () => import("@/views/DashboardView.vue"),
        meta: {
          title: "仪表板",
          requiresAuth: true,
        },
      },
      {
        path: "organizations",
        name: "Organizations",
        component: () => import("@/views/OrganizationsView.vue"),
        meta: {
          title: "组织管理",
          requiresAuth: true,
        },
      },
      {
        path: "organizations/:id",
        name: "OrganizationDetail",
        component: () => import("@/views/OrganizationDetailView.vue"),
        meta: {
          title: "组织详情",
          requiresAuth: true,
        },
      },
      {
        path: "dictionary-versions",
        name: "DictionaryVersions",
        component: () => import("@/views/DictionaryVersionsView.vue"),
        meta: {
          title: "词典版本管理",
          requiresAuth: true,
        },
      },
      {
        path: "dictionary-versions/:id",
        name: "DictionaryVersionDetail",
        component: () => import("@/views/DictionaryVersionDetailView.vue"),
        meta: {
          title: "词典版本详情",
          requiresAuth: true,
        },
      },
      {
        path: "studies",
        name: "Studies",
        component: () => import("@/views/StudiesView.vue"),
        meta: {
          title: "研究管理",
          requiresAuth: true,
        },
      },
      {
        path: "studies/:id",
        name: "StudyDetail",
        component: () => import("@/views/StudyDetailView.vue"),
        meta: {
          title: "研究详情",
          requiresAuth: true,
        },
      },
      {
        path: "sites",
        name: "Sites",
        component: () => import("@/views/SitesView.vue"),
        meta: {
          title: "研究中心管理",
          requiresAuth: true,
        },
      },
      {
        path: "study-roles",
        name: "StudyRoles",
        component: () => import("@/views/StudyRolesView.vue"),
        meta: {
          title: "研究角色管理",
          requiresAuth: true,
        },
      },
      {
        path: "study-members",
        name: "StudyMembers",
        component: () => import("@/views/StudyMembersView.vue"),
        meta: {
          title: "研究团队管理",
          requiresAuth: true,
        },
      },
      {
        path: "participants",
        name: "Participants",
        component: () => import("@/views/ParticipantsView.vue"),
        meta: {
          title: "参与者管理",
          requiresAuth: true,
        },
      },
      {
        path: "participants/:id",
        name: "ParticipantDetail",
        component: () => import("@/views/ParticipantDetailView.vue"),
        meta: {
          title: "参与者详情",
          requiresAuth: true,
        },
      },
      {
        path: "forms/:formInstanceId/edit",
        name: "ParticipantFormWorkspaceEdit",
        component: () =>
          import("@/views/forms/ParticipantFormWorkspaceView.vue"),
        meta: {
          title: "表单工作台",
          requiresAuth: true,
        },
      },
      {
        path: "forms/:formInstanceId/preview",
        name: "ParticipantFormWorkspacePreview",
        component: () =>
          import("@/views/forms/ParticipantFormWorkspaceView.vue"),
        meta: {
          title: "表单只读工作台",
          requiresAuth: true,
        },
      },
      {
        path: "clinscan/batches",
        name: "VlBatchReview",
        component: () => import("@/views/VlBatchReviewView.vue"),
        meta: {
          title: "ClinScan 批次核对",
          requiresAuth: true,
        },
      },
      {
        path: "clinscan/batches/:batchRecordId",
        name: "VlBatchDetail",
        component: () => import("@/views/VlBatchDetailView.vue"),
        meta: {
          title: "批次详情",
          requiresAuth: true,
        },
      },
      {
        path: CRF_VERSION_MANAGEMENT_ROUTE_PATH,
        name: CRF_VERSION_MANAGEMENT_ROUTE_NAME,
        component: () => import("@/views/CrfVersionManagement.vue"),
        meta: {
          title: "CRF 管理",
          requiresAuth: true,
        },
      },
      {
        path: CRF_VERSION_DETAIL_ROUTE_PATH,
        name: CRF_VERSION_DETAIL_ROUTE_NAME,
        component: () => import("@/views/CrfVersionDetailView.vue"),
        meta: {
          title: "CRF 版本详情",
          requiresAuth: true,
        },
      },
      {
        path: "crf-mapping",
        name: "CrfMapping",
        component: () => import("@/views/CrfMappingView.vue"),
        meta: {
          title: "CRF 版本映射",
          requiresAuth: true,
        },
      },
      {
        path: "query-recompute",
        name: "QueryRecomputeList",
        component: () => import("@/views/QueryRecomputeListView.vue"),
        meta: {
          title: "Query 字段重计算",
          requiresAuth: true,
        },
      },
      {
        path: "query-recompute/:id",
        name: "QueryRecomputeDetail",
        component: () => import("@/views/QueryRecomputeDetailView.vue"),
        meta: {
          title: "Query 重计算详情",
          requiresAuth: true,
        },
      },
      {
        path: "study-context-demo",
        name: "StudyContextDemo",
        component: () => import("@/views/StudyContextDemoPage.vue"),
        meta: {
          title: "Study Context Demo",
          requiresAuth: true,
        },
      },
      {
        path: "profile",
        name: "Profile",
        component: () => import("@/views/ProfileView.vue"),
        meta: {
          title: "个人中心",
          requiresAuth: true,
        },
      },
      {
        path: "settings",
        name: "Settings",
        component: () => import("@/views/SettingsView.vue"),
        meta: {
          title: "系统设置",
          requiresAuth: true,
        },
      },
      {
        path: "admin/system-config",
        name: "SystemConfig",
        component: () => import("@/views/admin/SystemConfigView.vue"),
        meta: {
          title: "系统配置管理",
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        path: "admin/data-export",
        name: "AdminDataExport",
        component: () => import("@/views/admin/DataExportView.vue"),
        meta: {
          title: "数据库导出",
          requiresAuth: true,
          requiresAdmin: true,
        },
      },
      {
        path: "log-form-test",
        name: "LogFormTest",
        component: () => import("@/views/LogFormTestView.vue"),
        meta: {
          title: "日志表单功能测试",
          requiresAuth: true,
        },
      },
    ],
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: () => import("@/views/NotFoundView.vue"),
    meta: {
      title: "页面未找到",
      requiresAuth: false,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
});

// Track if study context has been initialized
let studyContextInitialized = false;

// Navigation guard
router.beforeEach(async (to, _from, next) => {
  console.log("[Router Guard] Navigation to:", to.path, "from:", _from.path);

  // Set page title
  document.title = to.meta.title ? `${to.meta.title} - ClinVant` : "ClinVant";

  const authStore = useAuthStore();
  const requiresAuth = to.meta.requiresAuth as boolean;
  const requiresAdmin = to.meta.requiresAdmin as boolean;

  console.log(
    "[Router Guard] requiresAuth:",
    requiresAuth,
    "requiresAdmin:",
    requiresAdmin,
  );

  // Check if using BFF mode
  const isBffMode = (import.meta as any).env?.VITE_DEV_USE_BFF === "true";
  console.log("[Router Guard] BFF mode:", isBffMode);

  // Check authentication (BFF mode: check user; Token mode: check token)
  const token = localStorage.getItem("token");
  const isAuthenticated = isBffMode ? !!authStore.user : !!token;
  console.log(
    "[Router Guard] token exists:",
    !!token,
    "authStore.user exists:",
    !!authStore.user,
    "isAuthenticated:",
    isAuthenticated,
  );

  if (requiresAuth && !isAuthenticated) {
    console.log("[Router Guard] Not authenticated, redirecting to /login");
    next("/login");
    return;
  }

  if (to.path === "/login" && isAuthenticated) {
    console.log(
      "[Router Guard] Already authenticated at /login, redirecting to /dashboard",
    );
    next("/dashboard");
    return;
  }

  // Check admin permission
  if (requiresAdmin) {
    const authStore = useAuthStore();
    if (!authStore.user) {
      authStore.initializeAuth();
    }

    // Only SUPER users can access admin routes
    if (authStore.user && authStore.user.role !== "SUPER") {
      next("/dashboard");
      return;
    }
  }

  // Restore study context for authenticated users (only once)
  // DON'T AWAIT - restore in background to avoid blocking navigation
  if (requiresAuth && isAuthenticated && !studyContextInitialized) {
    const studyContextStore = useStudyContextStore();

    // Initialize auth store first if needed
    if (!authStore.user) {
      authStore.initializeAuth();
    }

    // Restore study context if user is authenticated (non-blocking)
    if (authStore.user?.id) {
      studyContextInitialized = true;
      // Fire and forget - don't block navigation
      studyContextStore
        .restoreStudyContext()
        .then(() => {
          console.log("Study context restored successfully");
        })
        .catch((error) => {
          console.warn("Failed to restore study context:", error);
        });
    }
  }

  console.log("[Router Guard] Navigation allowed to:", to.path);
  next();
});

// Reset study context initialization flag on logout
router.afterEach((to) => {
  if (to.path === "/login") {
    studyContextInitialized = false;
  }
});

export default router;
